<template>
  <v-card v-if="haveDataToShow" :class="[margin]">
    <v-card-text>
      <h2 class="title mt-0 mb-3">{{ $t('candidate-student-profile-education-label', language) }}</h2>

      <div v-if="translation">
        <diploma-snippet v-for="diploma in translation.education" :key="diploma.id" :diploma="diploma" />
      </div>

      <v-divider
        class="my-3"
        v-if="translation && translation.education.length > 0 && profile.currentlySubscribedToStudyProgram !== null"
      />
      <ui-content class="sectionRow" v-if="profile.currentlySubscribedToStudyProgram !== null">
        <h3 class="subheading question">{{ $t('candidate-profile-currently-subscribed-to-study-program-label', language) }}</h3>
        <span>{{ getCurrentlySubscribedToStudyProgram }}</span>
      </ui-content>

      <v-divider
        class="my-3"
        v-if="
          ((translation && translation.education.length > 0) || profile.currentlySubscribedToStudyProgram !== null) &&
            profile.overallAverage !== null
        "
      />
      <ui-content class="sectionRow" v-if="profile.overallAverage !== null">
        <h3 class="subheading question">{{ $t('candidate-profile-overal-average-label', language) }}</h3>
        <span>{{ profile.overallAverage }}</span>
      </ui-content>

      <ui-content class="sectionRow" v-if="profile.lastYearAverage !== null">
        <h3 class="subheading question">{{ $t('candidate-profile-last-year-average-label', language) }}</h3>
        <span>{{ profile.lastYearAverage }}{{ getRatingSystem(profile.ratingSystem) }}</span>
      </ui-content>

      <v-divider class="my-3" v-if="profile.hasReportsAndDiplomasCopy !== null" />
      <ui-content class="sectionRow" v-if="profile.hasReportsAndDiplomasCopy !== null">
        <h3 class="subheading question">{{ $t('candidate-profile-report-and-diploma-copy', language) }}</h3>
        <span>{{ getYesNoLabel(profile.hasReportsAndDiplomasCopy) }}</span>
      </ui-content>

      <template v-if="profile.trainingLevel !== null">
        <v-divider class="my-3" />
        <ui-content class="sectionRow">
          <h3 class="subheading question">{{ $t('candidate-profile-training-level-label', language) }}</h3>
          <span>{{ getTrainingLevelLabel(profile.trainingLevel) }}</span>
        </ui-content>
      </template>

      <template v-if="hasTrainingRelated">
        <v-divider class="my-3" />
        <ui-content class="sectionRow">
          <h3 class="subheading question">{{ $t('candidate-profile-training-related-label', language) }}</h3>
        </ui-content>
        <div>
          <ui-content v-for="related in trainingRelated" :key="related">
            <p v-html="$htmlEnter(related)" class="specification" />
          </ui-content>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { Consts } from '@/helpers/consts'
import DiplomaSnippet from '@/components/candidate/profile-card/educational/diploma-snippet'

export default {
  components: {
    DiplomaSnippet
  },
  data() {
    return {
      language: 'fr' // Always french for now
    }
  },
  props: {
    profile: {
      type: Object,
      required: true
    },
    margin: {
      type: String,
      required: true
    }
  },
  computed: {
    haveDataToShow: function() {
      return (
        (this.translation && this.translation.education.length > 0) ||
        this.profile.currentlySubscribedToStudyProgram !== null ||
        this.profile.overallAverage !== null ||
        this.profile.hasReportsAndDiplomasCopy ||
        this.profile.trainingLevel !== null ||
        this.hasTrainingRelated
      )
    },
    translation: function() {
      return this.profile.translations.find(t => t.language === Consts.CONTENT_LANGUAGE_FR)
    },
    hasTrainingRelated: function() {
      return this.profile.trainingRelated !== null && this.profile.trainingRelated.length > 0
    },
    trainingRelated: function() {
      const relateds = []

      this.profile.trainingRelated.forEach(related => {
        switch (related) {
          case Consts.TRAINING_RELATED_MATHEMATICS:
            relateds.push('- ' + this.$t('training-related-mathematics', this.language))
            break
          case Consts.TRAINING_RELATED_SCIENCE:
            relateds.push('- ' + this.$t('training-related-science', this.language))
            break
          case Consts.TRAINING_RELATED_PHYSICAL:
            relateds.push('- ' + this.$t('training-related-physical', this.language))
            break
          case Consts.TRAINING_RELATED_CHEMISTRY:
            relateds.push('- ' + this.$t('training-related-chemistry', this.language))
            break
          case Consts.TRAINING_RELATED_NONE:
            relateds.push('- ' + this.$t('training-related-none', this.language))
            break
        }
      })

      return relateds
    },
    getCurrentlySubscribedToStudyProgram: function() {
      const level = this.getTrainingLevelLabel(this.profile.currentStudyProgramLevel)
      return this.getYesNoLabel(this.profile.currentlySubscribedToStudyProgram) + (level ? ', ' + level : '')
    }
  },
  methods: {
    getYesNoLabel: function(value) {
      return value ? this.$t('yes-option', this.language) : this.$t('no-option', this.language)
    },
    getTrainingLevelLabel: function(value) {
      if (value === Consts.TRAINING_LEVEL_PRE_UNIVERSITY) {
        return this.$t('candidate-profile-training-level-pre-university', this.language)
      } else if (value === Consts.TRAINING_LEVEL_BACHELOR) {
        return this.$t('candidate-profile-training-level-bachelor', this.language)
      } else if (value === Consts.TRAINING_LEVEL_PROFESSIONAL_MASTERS) {
        return this.$t('candidate-profile-training-level-master-pro', this.language)
      } else if (value === Consts.TRAINING_LEVEL_DOCTORATE) {
        return this.$t('candidate-profile-training-level-doctorate', this.language)
      } else if (value === Consts.TRAINING_LEVEL_NONE) {
        return this.$t('candidate-profile-training-level-none', this.language)
      }
    },
    getRatingSystem: function(value) {
      if (value === Consts.RATING_SYSTEM_STUDY_FIVE) {
        return '/5'
      } else if (value === Consts.RATING_SYSTEM_STUDY_TEN) {
        return '/10'
      } else if (value === Consts.RATING_SYSTEM_STUDY_TWENTY) {
        return '/20'
      } else if (value === Consts.RATING_SYSTEM_STUDY_HUNDRED) {
        return '/100'
      }
      return ''
    }
  }
}
</script>

<style lang="stylus">
.question {
  width: 77%;
}

.sectionRow {
  display: flex;
  justify-content: space-between;
}

.specification {
  font-style: italic;
}
</style>
